"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDataFlowOfRelativeValues = void 0;
var tslib_1 = require("tslib");
var operators_1 = require("rxjs/operators");
var utils_1 = require("../../../utils");
var is_bbox_parts_1 = require("../../functions/boolean-fn/is-bbox-parts");
var point_is_in_paint_object_1 = require("../../functions/boolean-fn/point-is-in-paint-object");
var bbox_to_edges_1 = require("../../functions/convert/bbox-to-edges");
var bbox_to_points_1 = require("../../functions/convert/bbox-to-points");
var canvas_object_to_cursor_type_1 = require("../../functions/convert/canvas-object-to-cursor-type");
var bboxes_relative_reducer_1 = require("../../functions/reducer/bboxes-relative-reducer");
var drag_state_reducer_1 = require("../../functions/reducer/drag-state-reducer");
var resize_rect_1 = require("../../functions/resize-rect");
var shrink_to_fit_in_img_1 = require("../../functions/shrink-to-fit-in-img");
var bbox_1 = require("../../types/record/bbox");
var bbox_face_1 = require("../../types/record/bbox-face");
var bboxes_relative_1 = require("../../types/record/bboxes-relative");
var drag_state_1 = require("../../types/record/drag-state");
/*************************************************************************************
    この関数内で現れる位置データはすべてvirtualImgRectAbsolute（ズーム後の画像を表す長方形）を1x1とする相対サイズである．
    TODO: 画像サイズに対応するピクセル値の値であるpointerPosPixel$が追加されたので，ソースコードを整理する．
   *************************************************************************************/
var useDataFlowOfRelativeValues = function (action$, mouseEvent$, mouseLeftIsDown$, selectedIdInput$, bboxesInput$, paintObjectsInput$, pointerPos$, pointerPosPixel$, pointHalfSize$, highlightIdInput$, mode$, visibleLabels$, selectedLabel$, idMaker) {
    /**
     * @description
     * （`***Copy$`について）
     * dataflowの下流の要素の値を上流側で参照する必要があるため用意
     * 無限ループの発生を防ぐため発火は無視する必要がある
     * （＝`withLatestFrom`等の値の参照のみ行うオペレーター内で使用）
     */
    var _a = tslib_1.__read((0, utils_1.useStateAsStream)(undefined), 2), grabbingObjectCopy$ = _a[0], setGrabbingObjectCopy = _a[1];
    /**
     * @description マウスを最後にクリックした場所．マウスを押した時に発火．
     */
    var startPos$ = (0, utils_1.useStream)(mouseEvent$.pipe((0, utils_1.filterValue)('left-down'), (0, utils_1.asValueFrom)(pointerPos$.pipe((0, utils_1.filterNotUndefined)()))));
    /**
     * @description マウスを最後に離した場所．マウスを離した時に発火．
     */
    var endPos$ = (0, utils_1.useStream)(mouseEvent$.pipe((0, utils_1.filterValue)('left-up'), (0, utils_1.asValueFrom)(pointerPos$.pipe((0, utils_1.filterNotUndefined)()))));
    /**
     * @description 現在ドラッグ中かどうか，ドラッグ開始・終了位置，XY変化量を発火．
     */
    var dragState$ = (0, utils_1.useDataStream)((0, drag_state_1.IDragState)(), (0, utils_1.mergeTyped)(mouseEvent$.pipe((0, utils_1.filterValue)('leave'), (0, operators_1.map)(function (op) { return ({ op: op, pos: (0, utils_1.IPos)() }); })), startPos$.pipe((0, operators_1.map)(function (pos) { return ({ op: 'start', pos: pos }); })), endPos$.pipe((0, operators_1.map)(function (pos) { return ({ op: 'end', pos: pos }); })), pointerPos$.pipe((0, utils_1.filterNotUndefined)(), (0, utils_1.filterByLatest)(mouseLeftIsDown$), (0, operators_1.map)(function (pos) { return ({ op: 'current', pos: pos }); }))).pipe((0, operators_1.scan)(drag_state_reducer_1.dragStateReducer, (0, drag_state_1.IDragState)())));
    /**
     * @description
     * 新しいbboxを追加する際の確定前の半透明の長方形（ドラッグ中のみ表示）
     */
    var addBboxTemporaryRect$ = (0, utils_1.useDataStream)((0, utils_1.IRect)(), dragState$.pipe((0, operators_1.withLatestFrom)(mode$), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), dragState = _b[0], mode = _b[1];
        return dragState.dragging && mode === 'rectbox-editor-add-bbox'
            ? (0, utils_1.normalizeRect)((0, utils_1.IRect)({
                top: dragState.start.y,
                left: dragState.start.x,
                width: dragState.vector.x,
                height: dragState.vector.y
            }))
            : (0, utils_1.IRect)();
    })));
    /**
     * @description
     * bboxをリサイズする際の確定前の長方形（ドラッグ中のみ表示）
     */
    var resizeBboxTemporaryRect$ = (0, utils_1.useDataStream)((0, utils_1.IRect)(), dragState$.pipe((0, operators_1.withLatestFrom)(mode$), (0, operators_1.withLatestFrom)(grabbingObjectCopy$), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), _c = tslib_1.__read(_b[0], 2), dragState = _c[0], mode = _c[1], g = _b[1];
        if (g === undefined ||
            g === 'background' ||
            g.type === 'face' ||
            g.type === 'paint') {
            return (0, utils_1.IRect)();
        }
        else {
            if (dragState.dragging && mode === 'rectbox-editor-resize-bbox') {
                return (0, shrink_to_fit_in_img_1.shrinkToFitInImgRelative)((0, resize_rect_1.resizeRect)(dragState.vector, g).rect);
            }
            else {
                return (0, utils_1.IRect)();
            }
        }
    })));
    /**
     * @description
     * 自動セグメンテーション対象領域を選択する際に表示する半透明の長方形
     */
    var cropTemporaryRect$ = (0, utils_1.useDataStream)((0, utils_1.IRect)(), dragState$.pipe((0, operators_1.withLatestFrom)(mode$), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), dragState = _b[0], mode = _b[1];
        return dragState.dragging && mode === 'auto-segmentation-crop'
            ? (0, utils_1.normalizeRect)((0, utils_1.IRect)({
                top: dragState.start.y,
                left: dragState.start.x,
                width: dragState.vector.x,
                height: dragState.vector.y
            }))
            : (0, utils_1.IRect)();
    })));
    /**
     * @description
     * 新規作成されたbbox（画像を1x1としたときの相対サイズ）
     */
    var addBbox$ = (0, utils_1.useStream)(endPos$.pipe((0, utils_1.filterByAll)(mode$.pipe((0, operators_1.pairwise)(), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), prev = _b[0], curr = _b[1];
        return prev === 'rectbox-editor-add-bbox' &&
            curr === 'rectbox-editor-standby';
    })), grabbingObjectCopy$.pipe((0, operators_1.pairwise)(), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), prev = _b[0], curr = _b[1];
        return prev !== undefined &&
            (prev === 'background' ||
                prev.type === 'face' ||
                prev.type === 'paint') &&
            curr === undefined;
    }))), (0, operators_1.withLatestFrom)(startPos$), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), endPos = _b[0], startPos = _b[1];
        return (0, shrink_to_fit_in_img_1.shrinkToFitInImgRelative)((0, utils_1.RectFromXYs)([startPos.x, endPos.x], [startPos.y, endPos.y]));
    }), (0, operators_1.filter)(utils_1.isValidRect), (0, operators_1.withLatestFrom)(selectedLabel$), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), rect = _b[0], selectedLabel = _b[1];
        return (0, bbox_1.IBbox)({ id: idMaker(), rect: rect, label: selectedLabel });
    })));
    /**
     * @description
     * リサイズ後のbbox（画像を1x1としたときの相対サイズ）
     */
    var resizeBbox$ = (0, utils_1.useStream)(endPos$.pipe((0, utils_1.filterByAll)(mode$.pipe((0, operators_1.pairwise)(), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), prev = _b[0], curr = _b[1];
        return prev === 'rectbox-editor-resize-bbox' &&
            curr === 'rectbox-editor-standby';
    }))), (0, utils_1.asValueFrom)(grabbingObjectCopy$.pipe((0, operators_1.pairwise)(), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), prev = _b[0], _ = _b[1];
        return prev;
    }))), (0, operators_1.filter)(is_bbox_parts_1.isBboxParts), (0, operators_1.withLatestFrom)(dragState$), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), g = _b[0], dragState = _b[1];
        return (0, resize_rect_1.resizeRect)(dragState.vector, g);
    })));
    /**
     * @description
     * 選択されているbboxのID（キーボードによる操作対象となる）
     */
    var selectedId$ = (0, utils_1.useDataStream)(undefined, (0, utils_1.combineLatestTyped)(mode$, (0, utils_1.mergeTyped)(selectedIdInput$, addBbox$.pipe((0, utils_1.get)('id')), resizeBbox$.pipe((0, utils_1.get)('id')), grabbingObjectCopy$.pipe((0, operators_1.filter)(is_bbox_parts_1.isBboxParts), (0, operators_1.map)(function (g) { return g.parent.id; })), action$.pipe((0, operators_1.filter)(function (e) { return ['remove-all', 'deselect-object'].includes(e.op); }), (0, operators_1.mapTo)(undefined)))).pipe((0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), mode = _b[0], id = _b[1];
        switch (mode) {
            case 'rectbox-editor-standby':
            case 'rectbox-editor-resize-bbox':
            case 'rectbox-editor-add-bbox':
            case 'resize-window':
            case 'auto-segmentation-crop':
            case 'auto-segmentation-standby':
                return id;
            case 'move-image-standby':
            case 'move-image-dragging':
            case 'readonly':
                return undefined;
        }
    })));
    var removeBbox$ = (0, utils_1.useStream)(action$.pipe((0, utils_1.get)('op'), (0, utils_1.filterValue)('remove-selected'), (0, utils_1.asValueFrom)(selectedId$), (0, utils_1.filterNotUndefined)(), (0, utils_1.skipUnchangedImmutable)()));
    /**
     * @description
     * 選択した自動セグメンテーション対象領域（画像を1x1としたときの相対サイズ）
     */
    var cropRect$ = (0, utils_1.useStream)(endPos$.pipe((0, utils_1.filterByAll)(mode$.pipe((0, operators_1.pairwise)(), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), prev = _b[0], curr = _b[1];
        return prev === 'auto-segmentation-crop' &&
            curr === 'auto-segmentation-standby';
    })), grabbingObjectCopy$.pipe((0, operators_1.pairwise)(), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), prev = _b[0], curr = _b[1];
        return prev !== undefined &&
            (prev === 'background' ||
                prev.type === 'face' ||
                prev.type === 'paint') &&
            curr === undefined;
    }))), (0, operators_1.withLatestFrom)(startPos$), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), endPos = _b[0], startPos = _b[1];
        return (0, shrink_to_fit_in_img_1.shrinkToFitInImgRelative)((0, utils_1.RectFromXYs)([startPos.x, endPos.x], [startPos.y, endPos.y]));
    }), (0, operators_1.filter)(utils_1.isValidRect)));
    var bboxChange$ = (0, utils_1.useStream)((0, utils_1.mergeTyped)(bboxesInput$.pipe((0, operators_1.map)(function (input) { return ({ op: 'input', value: input }); }), (0, utils_1.skipUnchangedImmutable)()), addBbox$.pipe((0, operators_1.map)(function (v) { return ({ op: 'add', value: v }); })), resizeBbox$.pipe((0, operators_1.map)(function (v) { return ({ op: 'resize', value: v }); })), removeBbox$.pipe((0, operators_1.map)(function (v) { return ({ op: 'remove', value: v }); })), action$.pipe((0, operators_1.filter)(function (e) {
        return ['remove-all', 'undo', 'redo', 'reset-undo-history'].includes(e.op);
    }), (0, operators_1.map)(function (e) {
        return e;
    }))));
    /**
     * @description 画像を1x1としたときのサイズのbboxリスト
     */
    var bboxDatabase$ = (0, utils_1.useDataStream)((0, bboxes_relative_1.IBboxesRelative)(), bboxChange$.pipe((0, operators_1.scan)(bboxes_relative_reducer_1.bboxesRelativeReducer, (0, bboxes_relative_1.IBboxesRelativeInternal)()), (0, utils_1.skipUnchangedImmutable)(), (0, operators_1.map)(function (e) {
        return (0, bboxes_relative_1.IBboxesRelative)({
            lastOp: e.lastOp,
            list: e.history.get(e.historyIdx, bboxes_relative_1.defaultValue).toList()
        });
    })));
    var bboxes$ = (0, utils_1.useDataStream)((0, utils_1.IList)(), (0, utils_1.combineLatestTyped)(bboxDatabase$.pipe((0, utils_1.get)('list')), visibleLabels$).pipe((0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), bboxes = _b[0], visibleLabels = _b[1];
        return bboxes.filter(function (b) { return visibleLabels.includes(b.label); });
    })));
    /**
     * @description bboxの点の集合
     */
    var points$ = (0, utils_1.useDataStream)((0, utils_1.IList)(), bboxes$.pipe((0, operators_1.map)(function (bboxes) { return bboxes.flatMap(bbox_to_points_1.bbox2Points); })));
    /**
     * @description マウスカーソルに重なるbboxの点のうち最前面にあるもの
     * @todo 検索を高速化する
     */
    var pointOnCursor$ = (0, utils_1.useDataStream)(undefined, (0, utils_1.combineLatestTyped)(points$, pointerPos$, pointHalfSize$).pipe((0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 3), points = _b[0], pointerPos = _b[1], pointSize = _b[2];
        return pointerPos === undefined
            ? (0, utils_1.IList)()
            : points.filter(function (p) {
                return (0, utils_1.dist)(pointerPos.x, p.centerPos.x) <= pointSize.width &&
                    (0, utils_1.dist)(pointerPos.y, p.centerPos.y) <= pointSize.height;
            });
    }), (0, operators_1.map)(function (list) { return list.last(undefined); })));
    /**
     * @description bboxの辺の集合
     */
    var edges$ = (0, utils_1.useDataStream)((0, utils_1.IList)(), bboxes$.pipe((0, operators_1.map)(function (bboxes) { return bboxes.flatMap(bbox_to_edges_1.bbox2Edges); })));
    /**
     * @description マウスカーソルに重なるbboxの辺のうち最前面にあるもの
     * @todo 検索を高速化する
     */
    var edgeOnCursor$ = (0, utils_1.useDataStream)(undefined, (0, utils_1.combineLatestTyped)(edges$, pointerPos$, pointHalfSize$).pipe((0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 3), edges = _b[0], pointerPos = _b[1], pointSize = _b[2];
        return pointerPos === undefined
            ? edges.take(0)
            : edges.filter(function (edge) {
                switch (edge.location) {
                    // 横の場合
                    case 'n':
                    case 's':
                        return ((0, utils_1.dist)(pointerPos.x, edge.centerPos.x) <= edge.halfLength &&
                            (0, utils_1.dist)(pointerPos.y, edge.centerPos.y) <= pointSize.height);
                    // 縦の場合
                    case 'w':
                    case 'e':
                        return ((0, utils_1.dist)(pointerPos.x, edge.centerPos.x) <= pointSize.width &&
                            (0, utils_1.dist)(pointerPos.y, edge.centerPos.y) <= edge.halfLength);
                    default:
                        return true;
                }
            });
    }), (0, operators_1.map)(function (list) { return list.last(undefined); })));
    /**
     * @description マウスカーソルに重なるbboxの面のうち最前面にあるもの
     * @todo 検索を高速化する
     */
    var faceOnCursor$ = (0, utils_1.useDataStream)(undefined, (0, utils_1.combineLatestTyped)(bboxes$, pointerPos$).pipe((0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), bboxes = _b[0], pointerPos = _b[1];
        return pointerPos === undefined
            ? bboxes.take(0)
            : bboxes.filter(function (bb) { return (0, utils_1.pointIsInRect)(pointerPos, bb.rect); });
    }), (0, operators_1.map)(function (list) { return list.map(function (bbox) { return (0, bbox_face_1.IBboxFace)({ type: 'face', parent: bbox }); }); }), (0, operators_1.map)(function (list) { return list.last(undefined); })));
    /**
     * @description マウスカーソルに重なるpaintObjectのうち最前面にあるもの
     * @todo 検索を高速化する
     */
    var paintObjectOnCursor$ = (0, utils_1.useDataStream)(undefined, (0, utils_1.combineLatestTyped)(paintObjectsInput$, pointerPosPixel$).pipe((0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), paintObjects = _b[0], pointerPos = _b[1];
        return pointerPos === undefined
            ? paintObjects.take(0)
            : paintObjects.filter(function (p) { return (0, point_is_in_paint_object_1.pointIsInPaintObject)(pointerPos, p); });
    }), (0, operators_1.map)(function (list) { return list.last(undefined); })));
    /**
     * @description
     * マウスポインタ位置にあるオブジェクト（点 or 辺 or 背景）．
     * 最前面にあるものを一つ返す（辺より点が上，後から追加されたものが上）．
     */
    var canvasObjectOnCursor$ = (0, utils_1.useDataStream)('background', (0, utils_1.combineLatestTyped)(pointOnCursor$, edgeOnCursor$, faceOnCursor$, paintObjectOnCursor$, mode$).pipe((0, operators_1.map)(function (_a) {
        var _b, _c, _d, _e, _f;
        var _g = tslib_1.__read(_a, 5), pointOnCursor = _g[0], edgeOnCursor = _g[1], faceOnCursor = _g[2], paintObjectsOnCursor = _g[3], mode = _g[4];
        switch (mode) {
            case 'auto-segmentation-crop':
            case 'auto-segmentation-standby':
                return (_b = paintObjectsOnCursor !== null && paintObjectsOnCursor !== void 0 ? paintObjectsOnCursor : faceOnCursor) !== null && _b !== void 0 ? _b : 'background';
            case 'rectbox-editor-add-bbox':
            case 'rectbox-editor-resize-bbox':
            case 'rectbox-editor-standby':
                return ((_e = (_d = (_c = pointOnCursor !== null && pointOnCursor !== void 0 ? pointOnCursor : edgeOnCursor) !== null && _c !== void 0 ? _c : faceOnCursor) !== null && _d !== void 0 ? _d : paintObjectsOnCursor) !== null && _e !== void 0 ? _e : 'background');
            case 'resize-window':
            case 'move-image-dragging':
            case 'move-image-standby':
            case 'readonly':
                return (_f = faceOnCursor !== null && faceOnCursor !== void 0 ? faceOnCursor : paintObjectsOnCursor) !== null && _f !== void 0 ? _f : 'background';
            default:
                return 'background';
        }
    })));
    /**
     * @description
     * 現在掴んでいるオブジェクト
     */
    var grabbingObject$ = (0, utils_1.useDataStream)(undefined, mouseLeftIsDown$.pipe((0, operators_1.withLatestFrom)(canvasObjectOnCursor$), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), mouseIsDown = _b[0], canvasObjectOnCursor = _b[1];
        return mouseIsDown ? canvasObjectOnCursor : undefined;
    })));
    var highlightId$ = (0, utils_1.useDataStream)(undefined, (0, utils_1.combineLatestTyped)(mode$, (0, utils_1.mergeTyped)(highlightIdInput$, canvasObjectOnCursor$.pipe((0, operators_1.map)(function (obj) {
        if (obj === undefined || obj === 'background')
            return undefined;
        switch (obj.type) {
            case 'face':
                return obj.parent.id;
            case 'edge':
            case 'point':
                return undefined;
            case 'paint':
                return obj.id;
        }
    })))).pipe((0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), mode = _b[0], nextId = _b[1];
        switch (mode) {
            case 'readonly':
            case 'rectbox-editor-standby':
            case 'auto-segmentation-standby':
                return nextId;
            default:
                return undefined;
        }
    })));
    var cursorType$ = (0, utils_1.useDataStream)('default', (0, utils_1.combineLatestTyped)(mode$, canvasObjectOnCursor$).pipe((0, operators_1.withLatestFrom)(grabbingObject$), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), _c = tslib_1.__read(_b[0], 2), mode = _c[0], canvasObjectOnCursor = _c[1], grabbingObject = _b[1];
        switch (mode) {
            case 'rectbox-editor-standby':
                return (0, canvas_object_to_cursor_type_1.canvasObject2cursorType)(canvasObjectOnCursor, 'rectbox');
            case 'rectbox-editor-resize-bbox':
                return (0, canvas_object_to_cursor_type_1.canvasObject2cursorType)(grabbingObject, 'rectbox');
            case 'rectbox-editor-add-bbox':
            case 'auto-segmentation-crop':
            case 'auto-segmentation-standby':
                return (0, canvas_object_to_cursor_type_1.canvasObject2cursorType)(canvasObjectOnCursor, 'auto-segmentation');
            case 'move-image-standby':
                return 'grab';
            case 'move-image-dragging':
                return 'grabbing';
            case 'readonly':
            case 'resize-window':
                return 'default';
        }
    })));
    (0, utils_1.useStreamEffect)(grabbingObject$, setGrabbingObjectCopy);
    return {
        cropTemporaryRectRelative$: cropTemporaryRect$,
        addBboxTemporaryRectRelative$: addBboxTemporaryRect$,
        resizeBboxTemporaryRectRelative$: resizeBboxTemporaryRect$,
        selectedId$: selectedId$,
        bboxesRelative$: bboxes$,
        bboxDatabase$: bboxDatabase$,
        highlightId$: highlightId$,
        canvasObjectRelativeOnCursor$: canvasObjectOnCursor$,
        cursorType$: cursorType$,
        cropRect$: cropRect$
    };
};
exports.useDataFlowOfRelativeValues = useDataFlowOfRelativeValues;
